import {z} from 'zod';
import {id} from '../global/globalModels';

export const point = z.tuple([z.number(), z.number()]);
export type Point = z.infer<typeof point>;

export const characteristic = z.object({
    id: id,
    term: z.string(),
});
export type Characteristic = z.infer<typeof characteristic>;

export const resourceType = z.object({
    id: id,
    icon: z.string(),
    name: z.string(),
    pluralName: z.string(),
    /**
     * Whether a resource is displayed as an area.
     */
    hasArea: z.boolean(),
    /**
     * Whether the resource has a detail page.
     */
    detailPage: z.boolean(),
    /**
     * Whether a resource has a capacity (e.g. zones and meeting rooms).
     */
    hasCapacity: z.boolean(),
    /**
     * Whether a resource might be synced with connected calendar service.
     */
    hasCalendarSync: z.boolean(),
    /**
     * Whether a single resource can be booked multiple times in parallel.
     */
    hasMultiBooking: z.boolean(),
    /**
     * Whether a resource of the given type can be marked as favorite.
     */
    isFavorable: z.boolean(),
    /**
     * Whether a resource is visually communicating an "occupied" state
     * (e.g. seat = true; meeting room = false).
     */
    isPresence: z.boolean(),
    /**
     * Whether the resource might be booked in an individual timeframe besides predefined timeframes
     * (whole day, morning...), despite having hot desking disabled.
     */
    allowsIndividualTimes: z.boolean(),
    /**
     * Relative size of the resource markers on maps.
     */
    iconScaling: z.number(),
    /**
     * Determines the order between resource types in the UI (ascending)
     */
    priority: z.number(),
    /**
     * Whether the resource is bookable for a finite number of days in the future.
     */
    hasMaxBookingDays: z.boolean(),
    /**
     * Whether a catering service can be added to the resource booking.
     */
    catering: z.boolean(),
    /**
     * Whether the resource type allows booking queues.
     */
    hasBookingQueue: z.boolean(),
    /**
     * Whether the resource can be booked permanently by booking rules.
     */
    permanentBooking: z.boolean(),
    /**
     * Whether bookings for the resource are automatically deleted for HR absences.
     */
    deleteBookingsOnAbsence: z.boolean(),
    /**
     * Whether bookings need to be checked in.
     */
    hasCheckIn: z.boolean(),
});
export type ResourceType = z.infer<typeof resourceType>;

export const resourceTypeFilter = z.object({
    resourceType: id,
    offset: z.number().min(0),
    limit: z.number().min(0).nullable(),
    minCapacity: z.number().min(0),
    characteristics: z.array(id),
});
export type ResourceTypeFilter = z.infer<typeof resourceTypeFilter>;

export const resource = z.object({
    id: id,
    name: z.string(),
    imageUrl: z.string().nullable(),
    resourceType: id,
    blocked: z.boolean(),
    room: id,
    vertices: z
        .array(point)
        .min(1)
        .transform<[number, number][]>(vertices => {
            if (vertices.length !== 2) {
                return vertices;
            }

            const [[x1, y1], [x2, y2]] = vertices;

            return [
                [x1, y1],
                [x2, y1],
                [x2, y2],
                [x1, y2],
            ];
        }),
    characteristics: z.array(z.string()),
    capacity: z.number().min(0),
});
export type Resource = z.infer<typeof resource>;

export const resourceDetails = z.object({
    id: id,
    name: z.string(),
    resourceType: z.string(),
    location: id,
    locationName: z.string(),
    floor: id,
    floorName: z.string(),
    room: id,
    roomName: z.string(),
    capacity: z.number().min(0),
    characteristics: z.array(characteristic),
    isFavorite: z.boolean(),
});
export type ResourceDetails = z.infer<typeof resourceDetails>;

export const userFavoriteResource = id;
export type UserFavoriteResource = z.infer<typeof userFavoriteResource>;

export const resourceAccess = z.object({
    id: id,
    access: z.boolean(),
});
export type ResourceAccess = z.infer<typeof resourceAccess>;
